// internationalization
import { Trans, useTranslation } from 'react-i18next';

// tanstack
import { Link as RouterLink, useParams } from '@tanstack/react-router';

// material ui icons
import CloseIcon from '@mui/icons-material/Close';

// material ui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography,
} from '@mui/material';

/**
 * ReleaseNotesDialog - A scrollable dialog displaying release notes
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Callback when dialog is closed
 * @param {string} props.releaseNotes - The release notes content (HTML or markdown)
 */
const ReleaseNotesDialog = ({ open, onClose, releaseNotes }) => {
  const { t } = useTranslation(['feedback', 'common']);
  const { accountId } = useParams({ strict: false });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            maxHeight: '80vh',
          },
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h5" component="span" fontWeight={600}>
          {t('release_notes.title', { ns: 'feedback' })}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              mt: 2,
              mb: 1,
              fontWeight: 600,
            },
            '& h1': { fontSize: '1.75rem' },
            '& h2': { fontSize: '1.5rem' },
            '& h3': { fontSize: '1.25rem' },
            '& p': {
              mb: 1.5,
              lineHeight: 1.7,
            },
            '& ul, & ol': {
              mb: 1.5,
              pl: 3,
            },
            '& li': {
              mb: 0.5,
            },
            '& code': {
              bgcolor: 'action.hover',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
            },
            '& pre': {
              bgcolor: 'action.hover',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          }}
        >
          {releaseNotes ? (
            // If releaseNotes contains HTML, render it
            <div dangerouslySetInnerHTML={{ __html: releaseNotes }} />
          ) : (
            <Typography color="text.secondary">
              {t('release_notes.empty', { ns: 'feedback' })}
            </Typography>
          )}
        </Box>

        {accountId ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>
            <Trans
              t={t}
              ns="feedback"
              i18nKey="release_notes.faq_link_text"
              components={{
                faqLink: (
                  <Link
                    component={RouterLink}
                    onClick={onClose}
                    to="/$accountId/support"
                    params={{ accountId }}
                  />
                ),
              }}
            />
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          {t('actions.close', { ns: 'common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNotesDialog;
